import React, { ReactNode, useState } from "react";
import styled from "styled-components";

import { Transaction, Asset } from "~/types/transactions";
import { ILocalAsset } from "~/types";
import {
  BUSINESS_ASSET_CATEGORIES,
  EXTERNAL_TRANSACTION_CATEGORIES,
} from "../../../../utils";
import CategorySelect from "../../TransactionView/CategorySelect";
import VatCategorySelect from "../../TransactionView/VatCategorySelect";
import LocalAssetsViewer from "~/components/common/LocalAssetsViewer";
import { MatchButton } from "./styles";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const ReceiptPreview = styled.div`
  max-width: 600px;
`;

type BookAndMatchTransaction = (
  transaction: Transaction,
  vatCategoryCode: string,
  categoryCode: string
) => void;

type OnAssetsUpdate = (transactionId: string, assets: Asset[]) => void;

const ExpandedRow = ({
  transaction,
  bookAndMatchTransaction,
  onAssetsUpdate,
}: {
  transaction: Transaction;
  bookAndMatchTransaction: BookAndMatchTransaction;
  onAssetsUpdate: OnAssetsUpdate;
}) => {
  const [vatCategoryCode, setVatCategoryCode] = useState<
    string | null | undefined
  >(transaction.vatCategoryCode);
  const [categoryCode, setCategoryCode] = useState<string | null | undefined>(
    transaction.categoryCode
  );

  return (
    <Container>
      {transaction.assets?.length ? (
        <ReceiptPreview>
          <LocalAssetsViewer
            id={`belege-receipt-preview-${transaction.id}`}
            assets={transaction.assets}
            topPagination
            useReceiptRemoval
            stickyReceiptRemoval
            onDelete={(asset: Asset | ILocalAsset) => {
              // After unmatch/delete, drop the receipt from this row so the
              // "Has asset" badge clears and the freed receipt returns to the
              // Belege queue.
              const remaining = (transaction.assets || []).filter(
                (transactionAsset) => transactionAsset !== asset
              );
              onAssetsUpdate(transaction.id, remaining);
            }}
          />
        </ReceiptPreview>
      ) : null}
      <Wrapper>
        <VatCategorySelect
          value={vatCategoryCode}
          meta={transaction.vatCategoryCodeMeta}
          disabled={false}
          style={{
            marginRight: "15px",
            width: "285px",
          }}
          onChangeHandler={setVatCategoryCode}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <CategorySelect
            value={categoryCode}
            meta={transaction.categoryCodeMeta}
            transactionAmount={transaction.amount}
            disabledOptions={[
              ...EXTERNAL_TRANSACTION_CATEGORIES,
              ...BUSINESS_ASSET_CATEGORIES,
            ]}
            disabled={false}
            onChangeHandler={setCategoryCode}
            style={{
              marginRight: "15px",
              width: "200px",
            }}
          />
          <MatchButton
            data-test="matchedTransactionsList.matchButton"
            disabled={!(categoryCode && vatCategoryCode)}
            onClick={async () => {
              if (categoryCode && vatCategoryCode) {
                await bookAndMatchTransaction(
                  transaction,
                  vatCategoryCode,
                  categoryCode
                );
              }
            }}
          >
            Book & Match
          </MatchButton>
        </div>
      </Wrapper>
    </Container>
  );
};

export const renderExpandedRow = (
  transaction: Transaction,
  bookAndMatchTransaction: BookAndMatchTransaction,
  onAssetsUpdate: OnAssetsUpdate
): ReactNode => {
  return (
    <ExpandedRow
      transaction={transaction}
      bookAndMatchTransaction={bookAndMatchTransaction}
      onAssetsUpdate={onAssetsUpdate}
    />
  );
};
